import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const TEMPLATE_LABELS = { story: 'Story', overview: 'Overview' };

function AdminAboutPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in to manage pages.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/about-pages`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setPages(response.data.aboutPages);
      } else {
        setError(response.data.message || 'Failed to fetch pages.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching pages.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      const response = await axios.delete(`${apiUrl}/api/admin/about-pages/${id}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        fetchPages();
      } else {
        setError(response.data.message || 'Failed to delete page.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting page.');
    }
  };

  const filteredPages = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return pages.filter((p) => {
      const matchesTemplate = !templateFilter || p.template === templateFilter;
      const matchesSearch = !term || p.title.toLowerCase().includes(term) || p.slug.toLowerCase().includes(term);
      return matchesTemplate && matchesSearch;
    });
  }, [pages, templateFilter, searchTerm]);

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Other Pages</span>
                </li>
              </ul>
            </div>
            <Link to="/admin-about-pages/new" className="btn btn-main rounded-pill py-9">
              <i className="ph ph-plus me-2" /> Add Page
            </Link>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}

          <div className="card">
            <div className="card-header border-bottom border-gray-100 flex-between flex-wrap gap-12">
              <h5 className="mb-0">All Pages ({filteredPages.length})</h5>
              <div className="d-flex flex-wrap gap-12">
                <select
                  className="form-select w-auto"
                  value={templateFilter}
                  onChange={(e) => setTemplateFilter(e.target.value)}
                >
                  <option value="">All Templates</option>
                  <option value="story">Story</option>
                  <option value="overview">Overview</option>
                </select>
                <input
                  type="text"
                  className="form-control w-auto"
                  placeholder="Search title or path..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : pages.length === 0 ? (
                <div className="text-center py-40 text-gray-400">
                  No pages yet. Click "Add Page" to create your first one.
                </div>
              ) : filteredPages.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No pages match your filters.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Title</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Template</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Path</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Status</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPages.map((p) => (
                        <tr key={p.id}>
                          <td className="py-12 px-20">
                            <div className="d-flex align-items-center gap-12">
                              {p.cover_image ? (
                                <img
                                  src={`${apiUrl}/${p.cover_image}`}
                                  alt={p.title}
                                  style={{ width: 48, height: 36, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                                />
                              ) : (
                                <div style={{
                                  width: 48, height: 36, borderRadius: 6, background: '#f3f4f6',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                  <i className="ph ph-image text-gray-300" />
                                </div>
                              )}
                              <span className="text-15 fw-medium text-heading">{p.title}</span>
                            </div>
                          </td>
                          <td className="py-12 px-20">
                            <span className="badge bg-main-50 text-main-600 border-0 py-4 px-12 rounded-pill text-13 fw-medium">
                              {TEMPLATE_LABELS[p.template] || p.template}
                            </span>
                          </td>
                          <td className="py-12 px-20">
                            <code className="text-13">/{p.slug}</code>
                          </td>
                          <td className="py-12 px-20">
                            <span className={`badge border-0 py-4 px-12 rounded-pill text-13 fw-medium ${p.status === 1 ? 'bg-success-100 text-success-600' : 'bg-warning-100 text-warning-600'}`}>
                              {p.status === 1 ? 'Published' : 'Draft'}
                            </span>
                          </td>
                          <td className="py-12 px-20">
                            <div className="d-flex align-items-center gap-8">
                              <a
                                href={`https://ifmedu.in/${p.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-main rounded-pill py-4 px-12"
                                title="View live page"
                              >
                                <i className="ph ph-arrow-square-out" />
                              </a>
                              <Link
                                to={`/admin-about-pages/edit/${p.id}`}
                                className="btn btn-sm btn-outline-main rounded-pill py-4 px-12"
                              >
                                <i className="ph ph-pencil me-1" /> Edit
                              </Link>
                              <button
                                type="button"
                                className="btn btn-sm rounded-pill py-4 px-12"
                                style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff' }}
                                onClick={() => handleDelete(p.id, p.title)}
                              >
                                <i className="ph ph-trash me-1" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminAboutPages;
